// PATH: src/pages/Devocionales.jsx
// VERSION: 2.2 — Tags negro/blanco + filtro multiselect (dropdown en md- / chips en lg+)

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  Badge,
  Button,
  Input,
  HStack,
  Tag,
  TagLabel,
  VStack,
  Wrap,
  WrapItem,
  useDisclosure,
  Modal,
  Divider,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Flex,
  Spinner,
  useBreakpointValue,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  LightMode,
} from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon, DownloadIcon } from '@chakra-ui/icons';
import { FiShare2, FiImage, FiFileText } from 'react-icons/fi';
import html2pdf from 'html2pdf.js';
import { toJpeg } from 'html-to-image';
import { API } from '../utils/apiBase';
import ShareCardModal from '../components/common/ShareCardModal';
import { useFavorites } from '../utils/useFavorites';
import AudioPlayerButton from '../components/common/AudioPlayerButton';
import { IconButton, Switch, FormControl, FormLabel } from '@chakra-ui/react';
import { FiHeart } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';

const PAGE_SIZE = 6;

/* ---------- helpers ---------- */
function readingMinutes(d) {
  // admite forma plana (verse_text) y anidada (verse.text)
  const verseTxt = d.verse_text ?? d.verse?.text ?? '';
  const text = [d.title, d.summary, verseTxt, ...(d.content || [])].join(' ');
  const words = (text.match(/\w+/g) || []).length;
  return Math.max(1, Math.round(words / 200));
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function normalizeDevotionalsPayload(payload) {
  let raw = [];
  if (!payload) return [];
  if (Array.isArray(payload)) raw = payload;
  else if (Array.isArray(payload.items)) raw = payload.items;
  else if (Array.isArray(payload.devotionals)) raw = payload.devotionals;
  else if (Array.isArray(payload.data)) raw = payload.data;
  
  return raw.map(d => {
    let tags = d.tags || [];
    if (typeof tags === 'string') {
      try { tags = JSON.parse(tags); }
      catch(e) { tags = tags.split(',').map(t => t.trim()).filter(Boolean); }
    }
    return { ...d, tags };
  });
}

/* ---------- page ---------- */
export default function Devocionales() {
  const [query, setQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]); // multiselect
  const [page, setPage] = useState(1);
  const [showFavorites, setShowFavorites] = useState(false);
  const { favorites, toggleFavorite, isFavorite } = useFavorites('iglesia_favoritos_devocionales');

  // data remota
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const searchPlaceholder = useBreakpointValue({
    base: 'Buscar…',
    md: 'Buscar por título, versículo o resumen…',
  });

  const isMdOrBelow = useBreakpointValue({ base: true, md: true, lg: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const payload = await fetchJson(`${API}/devotionals?limit=1000`);
        if (!alive) return;
        const normalized = normalizeDevotionalsPayload(payload);
        setItems(normalized);
      } catch (e) {
        if (alive) setErr(e?.message || 'Error al cargar devocionales');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSeriesOpen, onOpen: onSeriesOpen, onClose: onSeriesClose } = useDisclosure();
  const [current, setCurrent] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [cachedImage, setCachedImage] = useState(null);
  const contentRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (items && items.length > 0) {
      const searchId = searchParams.get('searchId');
      if (searchId) {
        const found = items.find(d => (d.id || `${d.title}-${d.date}`) === searchId);
        if (found) {
          setCurrent(found);
          onOpen();
        }
        setSearchParams({});
      }
    }
  }, [items, searchParams, setSearchParams, onOpen]);

  const allTags = useMemo(() => {
    const s = new Set();
    (items || []).forEach((d) => (d.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return (items || [])
      .filter((d) => {
        const verseRef = (d.verse_reference ?? d.verse?.reference ?? '').toLowerCase();
        const verseTxt = (d.verse_text ?? d.verse?.text ?? '').toLowerCase();
        const hitQ =
          !q ||
          (d.title ?? '').toLowerCase().includes(q) ||
          (d.summary ?? '').toLowerCase().includes(q) ||
          verseRef.includes(q) ||
          verseTxt.includes(q);

        if (!selectedTags.length) {
          // sin filtro de tags → pasa solo por texto
          return hitQ;
        }

        const tags = d.tags || [];
        const hitTag = selectedTags.some((t) => tags.includes(t));
        const hitFav = showFavorites ? favorites.includes(d.id || `${d.title}-${d.date}`) : true;
        return hitQ && hitTag && hitFav;
      })
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [items, query, selectedTags, showFavorites, favorites]);

  const groupedItems = useMemo(() => {
    const groups = {};
    const result = [];
    filtered.forEach(d => {
      if (d.serie) {
        if (!groups[d.serie]) {
          const newGroup = { isSeries: true, serie: d.serie, parts: [] };
          groups[d.serie] = newGroup;
          result.push(newGroup);
        }
        groups[d.serie].parts.push(d);
      } else {
        result.push(d);
      }
    });
    // Ordenar las partes de cada serie por fecha ascendente (el más viejo primero)
    Object.values(groups).forEach(g => {
       g.parts.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
    });
    return result;
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(groupedItems.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = groupedItems.slice(start, start + PAGE_SIZE);
  
  const currentBg = '#ffffff';

  useEffect(() => {
    let alive = true;
    if (current && contentRef.current) {
      setCachedImage(null);
      setTimeout(async () => {
        try {
          const canvas = await html2canvas(contentRef.current, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: currentBg
          });
          const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
          if (alive) setCachedImage(dataUrl);
        } catch (e) {
          console.error('Error pre-generando Devocional', e);
        }
      }, 500); // give the modal 500ms to mount
    }
    return () => { alive = false; };
  }, [current]);

  const handleDownloadPDF = () => {
    if (!contentRef.current) return;
    const element = contentRef.current;
    
    const htmlFooter = element.querySelector('#pdf-html-footer');
    if (htmlFooter) htmlFooter.style.display = 'none';

    const opt = {
      margin: [15, 10, 25, 10], // top, right, bottom, left in mm
      filename: `${current?.title || 'Devocional'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };
    
    html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf) => {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        pdf.setDrawColor(200, 200, 200);
        // Draw line at pageHeight - 18mm
        pdf.line(10, pageHeight - 18, pageWidth - 10, pageHeight - 18);
        
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        pdf.text('SALA EVANGÉLICA BARRIO LEMPIRA', pageWidth / 2, pageHeight - 12, { align: 'center' });
        
        pdf.setFontSize(8);
        pdf.setTextColor(120, 120, 120);
        pdf.text('www.salaevangelica.com', pageWidth / 2, pageHeight - 7, { align: 'center' });
      }
      
      if (htmlFooter) htmlFooter.style.display = 'block';
    }).save();
  };

  const handleDownloadImage = async () => {
    if (!contentRef.current) return;
    
    if (cachedImage) {
      const link = document.createElement('a');
      link.download = `${current?.title || 'Devocional'}.jpg`;
      link.href = cachedImage;
      link.click();
      return;
    }

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: currentBg
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      
      setCachedImage(dataUrl);
      
      const link = document.createElement('a');
      link.download = `${current?.title || 'Devocional'}.jpg`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleVerMas = (d) => {
    if (d.isSeries) {
      setSelectedSeries(d);
      onSeriesOpen();
    } else {
      setCurrent(d);
      onOpen();
    }
  };

  const selectedTagsLabel = useMemo(() => {
    if (!selectedTags.length) return 'Todas las etiquetas';
    if (selectedTags.length === 1) return selectedTags[0];
    return `${selectedTags.length} etiquetas seleccionadas`;
  }, [selectedTags]);

  const activeTagBg = useColorModeValue('black', 'white');
  const activeTagColor = useColorModeValue('white', 'black');
  const subtleBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  if (loading) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <HStack justify="center" py={10}>
            <Spinner size="lg" />
            <Text> Cargando devocionales…</Text>
          </HStack>
        </Container>
      </Box>
    );
  }

  if (err) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <Heading size="md" color="red.600">
            Error al cargar devocionales
          </Heading>
          <Text color="gray.700" mt={2}>
            {String(err)}
          </Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="bg.default" flex="1">
      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        <Heading textAlign="center" mb={6} color="text.default">
          Devocionales
        </Heading>

        <VStack align="stretch" spacing={4} mb={6}>
          <Input
            borderColor="border.default"
            borderWidth="2px"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => {
              setPage(1);
              setQuery(e.target.value);
            }}
            bg="bg.card"
            _hover={{ borderColor: 'blue' }}
            _placeholder={{ color: 'text.muted' }}
          />

          <FormControl display="flex" alignItems="center">
            <Switch 
              id="favorites-mode" 
              colorScheme="blue" 
              isChecked={showFavorites}
              onChange={(e) => {
                setPage(1);
                setShowFavorites(e.target.checked);
              }}
            />
            <FormLabel htmlFor="favorites-mode" mb="0" ml={3} color="text.default" fontWeight="normal">
              Mostrar solo mis favoritos
            </FormLabel>
          </FormControl>

          {/* Filtro de tags: dropdown en md- / chips en lg+ */}
          {isMdOrBelow ? (
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                variant="outline"
                rightIcon={<ChevronDownIcon />}
                alignSelf="flex-start"
                borderColor="border.default"
                bg="bg.card"
                _hover={{
                  bg: 'black',
                  color: 'white',
                  borderColor: 'gray.700',
                }}
              >
                {selectedTagsLabel}
              </MenuButton>
              <MenuList maxH="260px" overflowY="auto">
                <MenuItem>
                  <Checkbox
                    isChecked={!selectedTags.length}
                    onChange={() => {
                      setPage(1);
                      setSelectedTags([]);
                    }}
                  >
                    Todas
                  </Checkbox>
                </MenuItem>
                {allTags.map((tag) => (
                  <MenuItem key={tag}>
                    <Checkbox
                      isChecked={selectedTags.includes(tag)}
                      onChange={() => {
                        setPage(1);
                        setSelectedTags((prev) =>
                          prev.includes(tag)
                            ? prev.filter((t) => t !== tag)
                            : [...prev, tag],
                        );
                      }}
                    >
                      {tag}
                    </Checkbox>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            <Flex wrap="wrap" gap={2} pb={2}>
              {/* Tag "Todos" */}
              <Tag
                variant="outline"
                bg={!selectedTags.length ? activeTagBg : 'bg.card'}
                color={!selectedTags.length ? activeTagColor : 'text.default'}
                borderColor={!selectedTags.length ? 'gray.700' : 'border.default'}
                borderWidth="2px"
                cursor="pointer"
                boxShadow={!selectedTags.length ? '0 0 0 1px rgba(15,23,42,0.5)' : 'none'}
                _hover={
                  !selectedTags.length
                    ? {
                      borderColor: 'gray.500',
                      boxShadow: '0 0 0 1px rgba(15,23,42,0.7)',
                    }
                    : {
                      bg: 'black',
                      color: 'white',
                      borderColor: 'gray.700',
                      boxShadow: '0 0 0 1px rgba(15,23,42,0.5)',
                    }
                }
                onClick={() => {
                  setSelectedTags([]);
                  setPage(1);
                }}
              >
                <TagLabel>Todos</TagLabel>
              </Tag>

              {/* Tags individuales */}
              {allTags.map((tag) => {
                const isActive = selectedTags.includes(tag);
                return (
                  <Tag
                    key={tag}
                    variant="outline"
                    bg={isActive ? activeTagBg : 'bg.card'}
                    color={isActive ? activeTagColor : 'text.default'}
                    borderColor={isActive ? 'gray.700' : 'border.default'}
                    borderWidth="2px"
                    cursor="pointer"
                    boxShadow={isActive ? '0 0 0 1px rgba(15,23,42,0.5)' : 'none'}
                    _hover={
                      isActive
                        ? {
                          borderColor: 'gray.500',
                          boxShadow: '0 0 0 1px rgba(15,23,42,0.7)',
                        }
                        : {
                          bg: activeTagBg,
                          color: activeTagColor,
                          borderColor: 'gray.700',
                          boxShadow: '0 0 0 1px rgba(15,23,42,0.4)',
                        }
                    }
                    onClick={() => {
                      setPage(1);
                      setSelectedTags((prev) =>
                        prev.includes(tag)
                          ? prev.filter((t) => t !== tag)
                          : [...prev, tag],
                      );
                    }}
                  >
                    <TagLabel>{tag}</TagLabel>
                  </Tag>
                );
              })}
            </Flex>
          )}
        </VStack>

        {pageItems.length === 0 ? (
          <Box
            bg="bg.card"
            border="1px solid"
            borderColor="border.default"
            borderRadius="md"
            p={6}
            textAlign="center"
          >
            <Text color="text.muted">No se encontraron devocionales con esos filtros.</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {pageItems.map((d) => {
              if (d.isSeries) {
                return (
                  <Box
                    key={`serie-${d.serie}`}
                    bg="bg.card"
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="sm"
                    p={5}
                    transition="all .25s ease"
                    _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
                    display="flex"
                    flexDirection="column"
                  >
                    <VStack align="start" spacing={3} position="relative" flex="1">
                      <Heading size="md" color="text.default" pr={8}>
                        {d.serie} ({d.parts.length} partes)
                      </Heading>
                      <Text color="text.muted" mb={4}>
                        Esta serie de devocionales está dividida en varias partes. Haz clic para ver el contenido completo de la serie.
                      </Text>
                      <Button colorScheme="blue" size="sm" onClick={() => handleVerMas(d)} mt="auto" alignSelf="flex-start">
                        Ver Serie
                      </Button>
                    </VStack>
                  </Box>
                );
              }

              const verseRef = d.verse_reference ?? d.verse?.reference;
              const verseTxt = d.verse_text ?? d.verse?.text;
              return (
                <Box
                  key={d.id || `${d.title}-${d.date}`}
                  bg="bg.card"
                  border="1px solid"
                  borderColor="border.default"
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="sm"
                  p={5}
                  transition="all .25s ease"
                  _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
                  display="flex"
                  flexDirection="column"
                >
                  <VStack align="start" spacing={3} position="relative" flex="1">
                    <Flex w="100%" justify="space-between" align="start">
                      <Heading size="md" color="text.default" noOfLines={2} pr={8}>
                        {d.title}
                      </Heading>
                      <IconButton
                        icon={isFavorite(d.id || `${d.title}-${d.date}`) ? <FaHeart color="#E53E3E" /> : <FiHeart />}
                        variant="ghost"
                        colorScheme="red"
                        size="sm"
                        aria-label="Favorito"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(d.id || `${d.title}-${d.date}`);
                        }}
                        position="absolute"
                        top={0}
                        right={0}
                      />
                    </Flex>
                    <Text color="text.muted" fontSize="sm">
                      {d.date ? new Date(d.date).toLocaleDateString() : ''} · {d.author} ·{' '}
                      {readingMinutes(d)} min
                    </Text>
                    {(d.serie || d.tags?.length > 0) ? (
                      <Wrap spacing={2} w="100%" minW={0}>
                        {d.serie && (
                          <WrapItem maxW="100%" minW={0}>
                            <Badge
                              variant="solid"
                              bg="purple.500"
                              color="white"
                              maxW="100%"
                              whiteSpace="normal"
                              wordBreak="break-word"
                              textAlign="left"
                            >
                              📚 Serie: {d.serie}
                            </Badge>
                          </WrapItem>
                        )}
                        {(d.tags || []).slice(0, 3).map((t, i) => (
                          <WrapItem key={`${t}-${i}`} maxW="100%" minW={0}>
                            <Badge
                              variant="solid"
                              colorScheme="blue"
                              maxW="100%"
                              whiteSpace="normal"
                              wordBreak="break-word"
                              textAlign="left"
                            >
                              {t}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    ) : null}
                    <Text color="text.muted" noOfLines={3}>
                      {d.summary}
                    </Text>
                    {(verseRef || verseTxt) && (
                      <Box
                        bg={subtleBg}
                        border="1px dashed"
                        borderColor="border.default"
                        borderRadius="md"
                        p={3}
                        w="100%"
                      >
                        {verseTxt && (
                          <Text
                            color="text.muted"
                            fontStyle="italic"
                            mb={1}
                            noOfLines={2}
                          >
                            "{verseTxt}"
                          </Text>
                        )}
                        {verseRef && (
                          <Text
                            color="text.muted"
                            fontSize="sm"
                            fontWeight="medium"
                          >
                            {verseRef}
                          </Text>
                        )}
                      </Box>
                    )}
                    <Button colorScheme="blue" size="sm" onClick={() => handleVerMas(d)} mt="auto" alignSelf="flex-start">
                      Ver más
                    </Button>
                  </VStack>
                </Box>
              );
            })}
          </SimpleGrid>
        )}

        {filtered.length > PAGE_SIZE && (
          <Flex mt={8} justify="center" align="center" gap={4}>
            <Button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              isDisabled={page === 1}
              variant="outline"
              leftIcon={<ArrowLeftIcon color="text.muted" />}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
            <Text color="text.muted">
              Página {page} de {totalPages}
            </Text>
            <Button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              isDisabled={page === totalPages}
              variant="outline"
              rightIcon={<ArrowRightIcon color="text.muted" />}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
          </Flex>
        )}
      </Container>

      {/* Modal para Series */}
      {selectedSeries && (
        <Modal isOpen={isSeriesOpen} onClose={onSeriesClose} size={{ base: 'full', md: 'lg' }} isCentered scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent bg="bg.card" color="text.default">
            <ModalHeader>{selectedSeries.serie}</ModalHeader>
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Text color="text.muted" mb={2}>Esta serie contiene {selectedSeries.parts.length} partes.</Text>
                {selectedSeries.parts.map((part) => (
                  <Box 
                    key={part.id || `${part.title}-${part.date}`} 
                    p={4} 
                    borderWidth="1px" 
                    borderColor="border.default" 
                    borderRadius="md"
                    _hover={{ bg: subtleBg, cursor: 'pointer' }}
                    onClick={() => {
                      onSeriesClose();
                      setCurrent(part);
                      onOpen();
                    }}
                  >
                    <Heading size="sm" mb={1}>{part.title}</Heading>
                    <Text fontSize="sm" color="text.muted" noOfLines={2}>{part.summary}</Text>
                  </Box>
                ))}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onSeriesClose} colorScheme="gray" size="sm">Cerrar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {current && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size={{ base: 'full', md: 'xl' }}
          isCentered
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent bg="bg.card" color="text.default" maxH="90vh">
            <ModalHeader display="flex" alignItems="center" gap={4} pr={12}>
              <Text flex="1" noOfLines={2}>{current.title}</Text>
              <AudioPlayerButton 
                id={current.id || `${current.title}-${current.date}`}
                title={current.title} 
                text={[current.summary, ...(current.content || [])].join(". ")} 
              />
              <IconButton
                icon={isFavorite(current.id || `${current.title}-${current.date}`) ? <FaHeart color="#E53E3E" /> : <FiHeart />}
                variant="ghost"
                colorScheme="red"
                size="sm"
                aria-label="Favorito"
                onClick={() => toggleFavorite(current.id || `${current.title}-${current.date}`)}
              />
            </ModalHeader>

            <ModalBody>
              {/* === VERSIÓN VISIBLE EN PANTALLA (Respeta tema oscuro/claro) === */}
              <VStack align="start" spacing={4}>
                <Text color="text.muted" fontSize="sm">
                  {current.date ? new Date(current.date).toLocaleDateString() : ''} ·{' '}
                  {current.author} · {readingMinutes(current)} min
                </Text>

                {current.serie && (
                  <Badge 
                    bg="purple.500" 
                    color="white" 
                    px={2} 
                    py={1} 
                    borderRadius="md" 
                    fontSize="xs"
                    whiteSpace="normal"
                    wordBreak="break-word"
                  >
                    📚 Serie: {current.serie}
                  </Badge>
                )}

                {(() => {
                  const verseRef = current.verse_reference ?? current.verse?.reference;
                  const verseTxt = current.verse_text ?? current.verse?.text;
                  return verseRef || verseTxt ? (
                    <Box
                      bg={subtleBg}
                      border="1px dashed"
                      borderColor="border.default"
                      borderRadius="md"
                      p={3}
                      w="100%"
                    >
                      {verseTxt && (
                        <Text color="text.muted" fontStyle="italic" mb={1}>
                          "{verseTxt}"
                        </Text>
                      )}
                      {verseRef && (
                        <Text
                          color="text.muted"
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          {verseRef}
                        </Text>
                      )}
                    </Box>
                  ) : null;
                })()}

                {/* Resumen */}
                {current.summary ? (
                  <Box
                    w="100%"
                    bg={subtleBg}
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="md"
                    p={4}
                  >
                    <Text fontSize="sm" fontWeight="semibold" color="text.muted" mb={2}>
                      Resumen
                    </Text>
                    <Text color="text.default">{current.summary}</Text>
                  </Box>
                ) : null}

                {/* Separador si hay ambas secciones */}
                {current.summary && (current.content || []).length ? (
                  <Divider w="100%" borderColor="border.default" />
                ) : null}

                {/* Contenido */}
                {(current.content || []).length ? (
                  <Box
                    w="100%"
                    bg="bg.card"
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="md"
                    p={4}
                  >
                    <VStack align="start" spacing={3}>
                      {(current.content || []).map((p, i) => (
                        <Text key={i} color="text.default">
                          {p}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                ) : null}

                {/* VISIBLE FOOTER */}
                <Box w="100%" pt={8} mt={4} borderTop="1px solid" borderColor="border.default" textAlign="center">
                  <Text fontSize="sm" fontWeight="bold" letterSpacing="widest" color="text.default">
                    SALA EVANGÉLICA BARRIO LEMPIRA
                  </Text>
                  <Text fontSize="xs" color="text.muted" mt={1}>
                    www.salaevangelica.com
                  </Text>
                </Box>
              </VStack>

              {/* === VERSIÓN OCULTA PARA CAPTURA (Siempre Light Mode y con Footer) === */}
              <Box position="absolute" top="-9999px" left="-9999px">
                <LightMode>
                  <Box 
                    ref={contentRef} 
                    w="190mm" 
                    bg="white" 
                    color="gray.800" 
                    display="block"
                    px={8}
                    py={6}
                  >
                    <Box>
                      <VStack align="start" spacing={4}>
                        <Heading size="lg" color="gray.900" mb={2}>{current.title}</Heading>
                        <Text color="gray.500" fontSize="sm">
                          {current.date ? new Date(current.date).toLocaleDateString() : ''} ·{' '}
                          {current.author} · {readingMinutes(current)} min
                        </Text>

                        {current.serie && (
                          <Text color="purple.600" fontSize="sm" fontWeight="bold">
                            📚 Serie: {current.serie}
                          </Text>
                        )}

                        {(() => {
                          const verseRef = current.verse_reference ?? current.verse?.reference;
                          const verseTxt = current.verse_text ?? current.verse?.text;
                          return verseRef || verseTxt ? (
                            <Box bg="gray.50" border="1px dashed" borderColor="gray.200" borderRadius="md" p={4} w="100%">
                              {verseTxt && <Text color="gray.700" fontStyle="italic" mb={2}>"{verseTxt}"</Text>}
                              {verseRef && <Text color="gray.600" fontSize="sm" fontWeight="bold">{verseRef}</Text>}
                            </Box>
                          ) : null;
                        })()}

                        {current.summary && (
                          <Box w="100%" bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} style={{ pageBreakInside: 'avoid' }}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2}>Resumen</Text>
                            <Text color="gray.800">{current.summary}</Text>
                          </Box>
                        )}

                        {current.summary && (current.content || []).length ? (
                          <Divider w="100%" borderColor="gray.200" />
                        ) : null}

                        {(current.content || []).length ? (
                          <Box w="100%" bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                            <VStack align="start" spacing={5}>
                              {(current.content || []).map((p, i) => (
                                <Text key={i} color="gray.800" fontSize="md" lineHeight="tall" style={{ pageBreakInside: 'avoid' }}>{p}</Text>
                              ))}
                            </VStack>
                          </Box>
                        ) : null}
                      </VStack>
                    </Box>

                    {/* FOOTER HTML (Oculto para PDF, solo se usa en JPG) */}
                    <Box id="pdf-html-footer" w="100%" pt={8} mt={8} borderTop="1px solid" borderColor="gray.200" textAlign="center">
                      <Text fontSize="sm" fontWeight="bold" letterSpacing="widest" color="gray.800">
                        SALA EVANGÉLICA BARRIO LEMPIRA
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        www.salaevangelica.com
                      </Text>
                    </Box>
                  </Box>
                </LightMode>
              </Box>
            </ModalBody>
            <ModalFooter display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <HStack spacing={2}>
                <Button 
                  colorScheme="accent" 
                  variant="outline"
                  leftIcon={<FiShare2 />} 
                  onClick={() => setIsShareModalOpen(true)}
                  size="sm"
                >
                  Tarjeta
                </Button>
                <Button 
                  colorScheme="blue" 
                  variant="ghost"
                  leftIcon={<FiFileText />} 
                  onClick={handleDownloadPDF}
                  size="sm"
                >
                  PDF
                </Button>
                <Button 
                  colorScheme="green" 
                  variant="ghost"
                  leftIcon={isGenerating ? <Spinner size="xs" /> : <FiImage />} 
                  onClick={handleDownloadImage}
                  isDisabled={isGenerating}
                  size="sm"
                >
                  JPG Full
                </Button>
              </HStack>
              <Button onClick={onClose} colorScheme="gray" size="sm">
                Cerrar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* MODAL COMPARTIR IMAGEN */}
      <ShareCardModal 
        isOpen={isShareModalOpen} 
        onClose={() => setIsShareModalOpen(false)} 
        text={current?.verse_text || current?.verse?.text || current?.summary} 
        reference={current?.verse_reference || current?.verse?.reference || current?.title} 
      />
    </Box>
  );
}
